/**
 * Applying a scene's outcomes with the bounds the sim enforces (§14).
 *
 * "Bounds enforced by the sim, never the model. Relationship deltas clamp.
 * Transfers validate against actual inventory. Appointments must reference
 * real places and times. Impossible outcomes are dropped — the prose still
 * stands. Never crash, never trust, never let a bad emission corrupt state."
 *
 * Every outcome is either applied or dropped with a reason. Nothing throws
 * out of here: a malformed payload is a drop, not a crash. The model never
 * touches the ledger directly — what lands in facts has passed through this.
 *
 * `visible` is the lying flag: a visible outcome moves the true layer and
 * the read layer; an invisible one moves the truth only (§12, §14).
 */

import { commitEvent } from './events.js';
import { mintAppointmentId } from './ids.js';
import { findEvent } from '../engine/memory/events.js';
import { writeFact } from '../engine/memory/facts.js';
import { formImpression, isMemoryTrigger, tagsForEvent } from '../engine/memory/impressions.js';
import { validateProposition } from './proposition.js';
import { isBefore, isValidTime } from './time.js';
import { TUNING } from './tuning.js';
import { actorExists, isFiniteNumber, livingNpc, subjectExists } from './validate.js';
import type {
  Actor,
  Appointment,
  AppointmentOutcome,
  BelievedEvent,
  Event,
  EventId,
  InformationOutcome,
  LocationId,
  MemoryOutcome,
  NPC,
  NpcBeliefs,
  NpcId,
  ObligationOutcome,
  Outcome,
  RelationshipAxes,
  RelationshipShiftOutcome,
  SceneBudget,
  SecretExposedOutcome,
  Stance,
  StanceChangeOutcome,
  StanceSubject,
  TransferOutcome,
  WorldState,
} from './types/index.js';

export interface OutcomeContext {
  /** The event the sim wrote for this scene, if any. Memories bind to it by default. */
  readonly sceneEvent: EventId | null;
  /** Who the player is in the scene with. */
  readonly counterpart: NpcId | null;
  /**
   * §14 per-scene budget already spent by earlier calls that belong to this
   * same logical scene (an in-progress multi-turn conversation, an
   * in-progress violent encounter). Omitted or absent means "nothing spent
   * yet" — a fresh scene. The caller is responsible for carrying
   * `OutcomeApplication.budget` forward between calls and resetting it only
   * when the scene actually ends; `applyOutcomes` itself never resets one on
   * its own, since it can't tell a scene's first call from its fifth.
   */
  readonly budget?: SceneBudget;
}

export interface DroppedOutcome {
  readonly outcome: Outcome;
  readonly reason: string;
}

export interface OutcomeApplication {
  readonly state: WorldState;
  readonly applied: readonly Outcome[];
  readonly dropped: readonly DroppedOutcome[];
  /** Events the outcomes wrote (transfers, exposures). */
  readonly events: readonly EventId[];
  /**
   * What this call leaves the per-scene budget at, `ctx.budget` included.
   * Callers that span a scene across several `applyOutcomes` calls must
   * pass this back in as the next call's `ctx.budget`.
   */
  readonly budget: SceneBudget;
}

type Result = { readonly state: WorldState; readonly events?: readonly EventId[] } | { readonly drop: string };

const drop = (reason: string): Result => ({ drop: reason });

/** Per-scene budgets, so several small shifts can't add up past the cap. */
interface Budget {
  readonly relationship: Record<string, number>;
  readonly stance: Record<string, number>;
}

const OUTCOME_TYPES: ReadonlySet<string> = new Set([
  'relationship_shift',
  'appointment',
  'obligation',
  'transfer',
  'information',
  'secret_exposed',
  'memory',
  'stance_change',
]);

export function applyOutcomes(
  state: WorldState,
  outcomes: readonly Outcome[],
  ctx: OutcomeContext,
): OutcomeApplication {
  let next = state;
  const applied: Outcome[] = [];
  const dropped: DroppedOutcome[] = [];
  const events: EventId[] = [];
  // Start from whatever this scene has already spent, not from zero: a
  // scene split across several calls (a multi-turn conversation, a
  // multi-beat fight) must share one cap, not get a fresh one per call.
  const budget: Budget = {
    relationship: { ...(ctx.budget?.relationship ?? {}) },
    stance: { ...(ctx.budget?.stance ?? {}) },
  };

  if (!Array.isArray(outcomes)) return { state, applied, dropped, events, budget };

  for (const outcome of outcomes) {
    let result: Result;
    try {
      result = applyOne(next, outcome, ctx, budget);
    } catch (error) {
      // Last line of defence. Validation above should make this unreachable,
      // but a bad emission must never take the world down with it.
      result = drop(`threw: ${error instanceof Error ? error.message : String(error)}`);
    }
    if ('drop' in result) {
      dropped.push({ outcome, reason: result.drop });
    } else {
      next = result.state;
      applied.push(outcome);
      if (result.events) events.push(...result.events);
    }
  }

  return { state: next, applied, dropped, events, budget };
}

function applyOne(state: WorldState, outcome: Outcome, ctx: OutcomeContext, budget: Budget): Result {
  if (typeof outcome !== 'object' || outcome === null) return drop('not an object');
  if (!OUTCOME_TYPES.has(outcome.type)) return drop(`unknown outcome type: ${String(outcome.type)}`);
  if (typeof outcome.payload !== 'object' || outcome.payload === null) return drop('payload missing');
  if (typeof outcome.visible !== 'boolean') return drop('visible flag missing');

  switch (outcome.type) {
    case 'relationship_shift':
      return applyRelationshipShift(state, outcome, budget);
    case 'appointment':
      return applyAppointment(state, outcome, ctx);
    case 'obligation':
      return applyObligation(state, outcome, ctx);
    case 'transfer':
      return applyTransfer(state, outcome);
    case 'information':
      return applyInformation(state, outcome, ctx);
    case 'secret_exposed':
      return applySecretExposed(state, outcome, ctx);
    case 'memory':
      return applyMemory(state, outcome, ctx);
    case 'stance_change':
      return applyStanceChange(state, outcome, budget);
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Clamp `delta` so that this scene's total on `key` stays within ±cap. */
function spend(budget: Record<string, number>, key: string, delta: number, cap: number): number {
  const spent = budget[key] ?? 0;
  const allowed = clamp(spent + delta, -cap, cap) - spent;
  budget[key] = spent + allowed;
  return allowed;
}

function updateNpc(state: WorldState, npc: NPC): WorldState {
  return { ...state, npcs: { ...state.npcs, [npc.id]: npc } };
}

function learnLocation(state: WorldState, location: LocationId): WorldState {
  if (state.player.knownLocations.includes(location)) return state;
  return {
    ...state,
    player: { ...state.player, knownLocations: [...state.player.knownLocations, location] },
  };
}

// ---------------------------------------------------------------------------
// relationship_shift — deltas clamp, per scene and absolutely
// ---------------------------------------------------------------------------

const AXES: readonly (keyof RelationshipAxes)[] = ['trust', 'respect', 'fear', 'warmth'];

function shiftAxes(beliefs: NpcBeliefs, deltas: Partial<RelationshipAxes>): NpcBeliefs {
  const relationship: { -readonly [K in keyof RelationshipAxes]: number } = { ...beliefs.relationship };
  for (const axis of AXES) {
    const d = deltas[axis];
    if (d === undefined) continue;
    relationship[axis] = clamp(relationship[axis] + d, TUNING.relationship.min, TUNING.relationship.max);
  }
  return { ...beliefs, relationship };
}

function applyRelationshipShift(state: WorldState, outcome: RelationshipShiftOutcome, budget: Budget): Result {
  const npc = livingNpc(state, outcome.target);
  if (!npc) return drop('target is not a living NPC');

  const deltas: { -readonly [K in keyof RelationshipAxes]?: number } = {};
  let any = false;
  for (const axis of AXES) {
    const raw = (outcome.payload as Record<string, unknown>)[axis];
    if (raw === undefined) continue;
    if (!isFiniteNumber(raw)) return drop(`${axis} is not a number`);
    deltas[axis] = spend(budget.relationship, `${npc.id}:${axis}`, raw, TUNING.relationship.perSceneCap);
    any = true;
  }
  if (!any) return drop('no relationship axes in payload');

  const truth = shiftAxes(npc.truth, deltas);
  const read = outcome.visible ? shiftAxes(npc.read, deltas) : npc.read;
  return { state: updateNpc(state, { ...npc, truth: { ...npc.truth, ...truth }, read: { ...npc.read, ...read } }) };
}

// ---------------------------------------------------------------------------
// appointment — real place, real time, real people
// ---------------------------------------------------------------------------

const PURPOSES: ReadonlySet<string> = new Set(['meet', 'deliver', 'intercede', 'hold', 'come_find_me']);

function applyAppointment(state: WorldState, outcome: AppointmentOutcome, ctx: OutcomeContext): Result {
  const p = outcome.payload;
  if (typeof p.place !== 'string' || !state.locations[p.place]) return drop('place is not a real location');
  if (!isValidTime(p.time)) return drop('time is malformed');
  if (isBefore(p.time, state.time)) return drop('time is in the past');
  if (!Array.isArray(p.participants) || p.participants.length === 0) return drop('no participants');
  for (const actor of p.participants) {
    if (!actorExists(state, actor)) return drop(`participant ${String(actor)} does not exist`);
  }
  if (!p.participants.includes(p.promisedBy)) return drop('promisedBy is not a participant');
  if (typeof p.purpose !== 'object' || p.purpose === null || !PURPOSES.has(p.purpose.kind)) {
    return drop('purpose is not one of the five shapes');
  }
  if (p.purpose.kind === 'intercede' && !livingNpc(state, p.purpose.with)) {
    return drop('intercede names an unknown person');
  }

  const minted = mintAppointmentId(state);
  const appointment: Appointment = {
    id: minted.id,
    place: p.place,
    time: p.time,
    participants: [...p.participants],
    purpose: p.purpose,
    promisedBy: p.promisedBy,
    status: { kind: 'pending' },
    madeOn: state.time,
    origin: ctx.sceneEvent,
    rescheduledFrom: null,
  };
  let next: WorldState = {
    ...minted.state,
    appointments: { ...minted.state.appointments, [appointment.id]: appointment },
  };
  // Somebody mentions a place, now there's a pin (§16).
  if (p.participants.includes('player')) next = learnLocation(next, p.place);
  return { state: next };
}

// ---------------------------------------------------------------------------
// obligation — someone owes someone
// ---------------------------------------------------------------------------

function applyObligation(state: WorldState, outcome: ObligationOutcome, ctx: OutcomeContext): Result {
  const p = outcome.payload;
  if (!actorExists(state, p.from)) return drop('from does not exist');
  if (!actorExists(state, p.to)) return drop('to does not exist');
  if (p.from === p.to) return drop('cannot owe yourself');
  if (typeof p.substance !== 'object' || p.substance === null) return drop('substance missing');
  if (p.substance.kind === 'money') {
    if (!isFiniteNumber(p.substance.amount) || p.substance.amount <= 0) return drop('money owed must be a positive number');
  } else if (p.substance.kind === 'service') {
    if (typeof p.substance.description !== 'string' || p.substance.description.trim() === '') return drop('service owed is empty');
  } else {
    return drop('substance is neither money nor a service');
  }
  if (p.deadline !== null && !isValidTime(p.deadline)) return drop('deadline is malformed');

  const written = writeFact(
    state,
    { kind: 'obligation', from: p.from, to: p.to, substance: p.substance, deadline: p.deadline, settled: false },
    ctx.sceneEvent,
  );
  return { state: written.state };
}

// ---------------------------------------------------------------------------
// transfer — validates against actual inventory, fires immediately
// ---------------------------------------------------------------------------

function cashOf(state: WorldState, actor: Actor): number {
  if (actor === 'player') return state.player.money;
  return state.npcs[actor]?.truth.money ?? 0;
}

function setCash(state: WorldState, actor: Actor, money: number): WorldState {
  if (actor === 'player') return { ...state, player: { ...state.player, money } };
  const npc = state.npcs[actor];
  if (!npc) return state;
  return updateNpc(state, { ...npc, truth: { ...npc.truth, money } });
}

function applyTransfer(state: WorldState, outcome: TransferOutcome): Result {
  const p = outcome.payload;
  if (!actorExists(state, p.from)) return drop('from does not exist');
  if (!actorExists(state, p.to)) return drop('to does not exist');
  if (p.from === p.to) return drop('cannot transfer to yourself');
  if (typeof p.what !== 'object' || p.what === null) return drop('nothing to transfer');

  if (p.what.kind === 'item') {
    // OPEN (§21): whether the player has an inventory beyond money is not
    // decided. Until it is, there is no inventory to validate against, so an
    // item transfer is impossible by definition and dropped — never guessed.
    return drop('item transfers are undecided (§21 inventory)');
  }
  if (p.what.kind !== 'money') return drop('unknown transfer kind');

  const amount = p.what.amount;
  if (!isFiniteNumber(amount) || amount <= 0) return drop('amount must be a positive number');
  const rounded = Math.round(amount * 100) / 100;
  if (cashOf(state, p.from) < rounded) return drop(`${p.from} does not have $${rounded}`);

  let next = setCash(state, p.from, Math.round((cashOf(state, p.from) - rounded) * 100) / 100);
  next = setCash(next, p.to, Math.round((cashOf(next, p.to) + rounded) * 100) / 100);

  const committed = commitEvent(next, {
    type: 'transfer',
    actor: p.from,
    target: p.to === 'player' ? { kind: 'player' } : { kind: 'npc', id: p.to },
    location: state.player.position.at,
    witnesses: [],
    outcome: 'success',
  });
  return { state: committed.state, events: [committed.event.id] };
}

// ---------------------------------------------------------------------------
// information — a claim, with a truth bit the player never sees
// ---------------------------------------------------------------------------

function applyInformation(state: WorldState, outcome: InformationOutcome, ctx: OutcomeContext): Result {
  const p = outcome.payload;
  if (!actorExists(state, p.from)) return drop('from does not exist');
  if (!actorExists(state, p.to)) return drop('to does not exist');
  if (p.from === p.to) return drop('cannot inform yourself');
  const bad = validateProposition(state, p.proposition);
  if (bad !== null) return drop(bad);
  if (typeof p.truthful !== 'boolean') return drop('truthful flag missing');

  const written = writeFact(
    state,
    { kind: 'claim', by: p.from, to: p.to, proposition: p.proposition, truthful: p.truthful },
    ctx.sceneEvent,
  );
  let next = written.state;

  // Being told about a place puts a pin on the map (§16).
  const prop = p.proposition;
  if (p.to === 'player') {
    for (const ref of [prop.subject, prop.object]) {
      if (ref.kind === 'location') next = learnLocation(next, ref.id);
    }
  }

  // An NPC told a true thing about an event now believes that event, from
  // the teller. A false one is only the claim: the record of the lie is
  // what discovery needs (§12).
  if (p.to !== 'player' && prop.subject.kind === 'event' && p.truthful) {
    const event = findEvent(next, prop.subject.id);
    if (event) {
      const formed = formImpression(next, p.to, believedEvent(event), {
        origin: event.id,
        source: { kind: 'told', by: p.from },
        tags: tagsForEvent(event),
      });
      next = formed.state;
    }
  }
  return { state: next };
}

function believedEvent(event: Event): BelievedEvent {
  return { type: event.type, actor: event.actor, target: event.target, location: event.location, at: event.at };
}

// ---------------------------------------------------------------------------
// secret_exposed — who now knows
// ---------------------------------------------------------------------------

function applySecretExposed(state: WorldState, outcome: SecretExposedOutcome, ctx: OutcomeContext): Result {
  const p = outcome.payload;
  if (typeof p.secret !== 'string' || !state.secrets[p.secret]) return drop('secret does not exist');
  if (!Array.isArray(p.nowKnownBy)) return drop('nowKnownBy is not a list');
  const secret = state.secrets[p.secret]!;
  const learners: Actor[] = [];
  for (const actor of p.nowKnownBy) {
    if (!actorExists(state, actor)) return drop(`${String(actor)} does not exist`);
    if (!secret.knownBy.includes(actor) && !learners.includes(actor)) learners.push(actor);
  }
  if (learners.length === 0) return drop('everyone named already knew');

  let next: WorldState = {
    ...state,
    secrets: { ...state.secrets, [secret.id]: { ...secret, knownBy: [...secret.knownBy, ...learners] } },
  };
  // Who let it out: if the player learned it, the counterpart told them;
  // otherwise the player is the one talking.
  const actor: Actor = learners.includes('player') && ctx.counterpart ? ctx.counterpart : 'player';
  const committed = commitEvent(next, {
    type: 'secret_exposed',
    actor,
    target: { kind: 'secret', id: secret.id },
    location: state.player.position.at,
    witnesses: learners.filter((a) => a !== actor),
    outcome: 'success',
  });
  next = committed.state;
  return { state: next, events: [committed.event.id] };
}

// ---------------------------------------------------------------------------
// memory — "will remember this", permanent by construction
// ---------------------------------------------------------------------------

function applyMemory(state: WorldState, outcome: MemoryOutcome, ctx: OutcomeContext): Result {
  const npc = livingNpc(state, outcome.target);
  if (!npc) return drop('target is not a living NPC');
  if (!isMemoryTrigger(outcome.payload.trigger)) return drop('trigger is not categorical');

  // Bind to a real event: the one named if it exists, else this scene's.
  const named = typeof outcome.payload.event === 'string' ? findEvent(state, outcome.payload.event) : undefined;
  const event = named ?? (ctx.sceneEvent ? findEvent(state, ctx.sceneEvent) : undefined);
  if (!event) return drop('no real event to remember');

  const firstHand =
    event.witnesses.includes(npc.id) ||
    event.actor === npc.id ||
    (event.target?.kind === 'npc' && event.target.id === npc.id);
  const formed = formImpression(state, npc.id, believedEvent(event), {
    origin: event.id,
    source: firstHand ? { kind: 'witnessed' } : { kind: 'told', by: 'player' },
    trigger: outcome.payload.trigger,
    tags: tagsForEvent(event),
  });
  return { state: formed.state };
}

// ---------------------------------------------------------------------------
// stance_change — disposition toward a faction, person or situation
// ---------------------------------------------------------------------------

function subjectKey(subject: StanceSubject): string {
  return subject.kind === 'player' ? 'player' : `${subject.kind}:${subject.id}`;
}

function shiftStance(beliefs: NpcBeliefs, subject: StanceSubject, delta: number): NpcBeliefs {
  const key = subjectKey(subject);
  const stances: Stance[] = [];
  let found = false;
  for (const s of beliefs.stances) {
    if (subjectKey(s.subject) === key) {
      found = true;
      stances.push({ ...s, disposition: clamp(s.disposition + delta, TUNING.stance.min, TUNING.stance.max) });
    } else {
      stances.push(s);
    }
  }
  if (!found) stances.push({ subject, disposition: clamp(delta, TUNING.stance.min, TUNING.stance.max) });
  return { ...beliefs, stances };
}

function applyStanceChange(state: WorldState, outcome: StanceChangeOutcome, budget: Budget): Result {
  const npc = livingNpc(state, outcome.target);
  if (!npc) return drop('target is not a living NPC');
  const p = outcome.payload;
  if (!subjectExists(state, p.subject)) return drop('subject does not exist');
  if (!isFiniteNumber(p.delta)) return drop('delta is not a number');
  const delta = spend(budget.stance, `${npc.id}:${subjectKey(p.subject)}`, p.delta, TUNING.stance.perSceneCap);

  const truth = shiftStance(npc.truth, p.subject, delta);
  const read = outcome.visible ? shiftStance(npc.read, p.subject, delta) : npc.read;
  return { state: updateNpc(state, { ...npc, truth: { ...npc.truth, ...truth }, read: { ...npc.read, ...read } }) };
}
